<!--
    ONE Deckung as a card: a titled header band with the Sparte accent, the level-1 figures, the
    relations it carries, and one expander for level 2. Chrome, spacing and colour are PrimeFlex
    utilities and theme tokens.
-->
@if (card) {

    <!--
        overflow-hidden is what lets the header band run edge to edge INSIDE the rounded border: the
        band carries no radius of its own, and the card clips its top corners. Without the clip the
        band's square corners would sit a pixel outside the rounded border and read as a misprint.
    -->
    <div class="surface-card border-1 surface-border border-round overflow-hidden">

        <!--
            The header BAND, TINTED IN THE CARD'S OWN SPARTE HUE (bg-{family}-50, picked in
            TypeScript from the sheet's one hue map — see tintClass).

            It was `surface-100`, and that neutral grey is what made a grid of a dozen cards read as
            one wall: the 0.25 rem spine was the only thing distinguishing three Unfall cards from
            two Hausrat cards, and at a glance nobody saw it. The band now carries the same hue as
            the spine over its full width, so the grouping is legible before a word is read. The
            title on top keeps `text-color` — a 50-step tint is exactly light enough for that.

            It still carries the card's only divider, `border-bottom-1 surface-border`.

            align-items-stretch lets the accent bar span the band's full height, padding included:
            the bar sits OUTSIDE the padded inner row precisely so it runs from the band's top edge
            to its bottom edge and reads as a spine, not as a floating tick.
        -->
        <div class="flex align-items-stretch border-bottom-1 surface-border"
             [ngClass]="tintClass">

            <span class="cm-ed-accent flex-none"
                  [ngClass]="accentClass"
                  aria-hidden="true"></span>

            <div class="flex flex-1 min-w-0 align-items-baseline justify-content-between gap-2"
                 [ngClass]="headClass">

                <!--
                    THE ellipsis. "Rechtsschutz — Verkehr" in a one-column card is the test case: it
                    must be cut, never wrapped, because a two-line heading in one card pushes the
                    figures of every card beside it out of alignment.

                    min-w-0 is load-bearing — a flex child defaults to min-width:auto and refuses to
                    shrink below its content, so text-overflow-ellipsis alone does nothing here.
                    title keeps the full heading reachable on hover.

                    `text-color` and not `text-primary`: the card TITLE is the name of a thing, and
                    the sheet spends its emphasis colour on section headings and on figures. A grid
                    of a dozen cards each shouting its own title in the accent colour is the
                    highlighter effect this palette exists to avoid.

                    THE TITLE CAN BE THE HIT. It is part of the host's haystack and is counted into
                    the "{n} Treffer" beside it, and until now the marker was the ONLY clue — the
                    number went up and no text on the card changed. `.cm-ed-hit-title` is the sheet's
                    one treatment for "the term is in this heading", the same class the section
                    heading and the row heading wear.

                    The mark sits on an INNER span. The outer element is a flex item with `flex-1`,
                    i.e. it owns the whole free width of the band; washing it would paint a bar across
                    the empty space beside a two-word title instead of marking the title. The inner
                    span is inline and hugs the text, and the ellipsis still bites, because
                    `text-overflow` works on the overflowing line box of the outer block and does not
                    care how its inline content is nested.

                    NOT the band. This card's header band carries the SPARTE TINT (bg-{family}-50) and
                    that tint is identity — which Sparte — so a search may not take it. It is also why
                    a `bg-primary-50` mark would not do here at all: the first Sparte of a delivery
                    wears `primary` itself, and a primary-50 mark on a primary-50 band is invisible.
                -->
                <span class="font-semibold text-color flex-1 min-w-0 overflow-hidden text-overflow-ellipsis white-space-nowrap"
                      [ngClass]="titleClass"
                      [title]="card.title"><span [class.cm-ed-hit-title]="ownTextHit">{{card.title}}</span></span>

                <!--
                    The Sparte line is dropped at xs — now BELOW 560 px, where the old ladder put that
                    floor at 420 px: in a card that narrow it takes the width the heading needs, and
                    the heading is what identifies the card. It stays pinned to the FLOOR of the ladder
                    and is not allowed to creep up it: from `sm` on the card is col-12, i.e. the widest
                    card on the whole ladder, and both lines fit with room over.

                    It is matched together with the title (`countTextHits(title, sub)` publishes one
                    total), so it takes the same mark: the screen states "the term is in this card's
                    own text" and never guesses which half of it. Inner span, as everywhere — the muted
                    utility stays on the outer element and the mark's own colour still wins, which it
                    must, since a marked sub that kept the muted tone measures 3.58:1 on the hit wash
                    against the body colour's 7.78:1.
                -->
                @if (card.sub && size !== 'xs') {
                    <span class="text-xs text-color-secondary white-space-nowrap"><span [class.cm-ed-hit-title]="ownTextHit">{{card.sub}}</span></span>
                }

                <!--
                    WHAT THIS CARD HOLDS FOR THE CURRENT TERM.

                    It carries far more weight here than a count normally would. Nothing on this sheet
                    dims or disappears during a search, so a card with three matches four rows down
                    looks exactly like a card with none — and this marker is the only thing on it that
                    says otherwise. It is what lets a reader scan a grid of a dozen cards and see where
                    to look, now that "everything else went grey" is no longer the cue.

                    Rendered only while a term is active and only when there is something to declare: a
                    "0 Treffer" on every card of a twelve-card grid would bury the one card that has
                    hits, which is the opposite of what it is for. Never at rest, so a card with no
                    search is byte-for-byte the card that shipped before.

                    NOT dropped at `xs`, unlike the Sparte line above it: the sub line is context a
                    narrow card can spare, while an unread hit is the thing the reader came for.
                -->
                @if (searching && card.hitCount) {
                    <span class="cm-ed-hits bg-primary-50 text-primary border-1 border-primary border-round-sm white-space-nowrap">{{card.hitCount}} Treffer</span>
                }

            </div>

        </div>

        <!-- The card's body. The band above carries the title; everything else lives in here. -->
        <div [ngClass]="padClass">

            <!--
                BASISDATEN — the figures a clerk reads first. ONE renderer for every pair on this
                screen (see mvs-cm-ed-kv-list), and the level is stated rather than assumed: the
                default would give the same result, but the pair of lists in this card is exactly
                where the two levels have to be told apart, so both say which one they are.
            -->
            <mvs-cm-ed-kv-list [fields]="card.level1"
                               [size]="size"
                               emphasis="basis"
                               [searching]="searching"></mvs-cm-ed-kv-list>

            <!--
                The relations this Deckung carries — the person it insures, the object it covers. They
                arrive as DATA (crossLinks), never parsed out of the heading, so a partner that names its
                Deckungen differently changes nothing here. Rendered as tags rather than links: the
                delivery states the relation, and where a click should lead is the owning view's call.
            -->
            @if (card.crossLinks.length) {
                <div class="flex flex-wrap align-items-center gap-1 mt-2">
                    <!--
                        Tracked by the edge's own identity, not by position: targetKey is documented as the
                        stable key of the group on the other end, and kind is part of it because the same
                        target may in principle be reached by two different edges.

                        SEVERITY CARRIES THE KIND. `info` for a person, `secondary` for an object — the
                        two severities the app already uses most (221 / 676 across features/), so a
                        reader tells a versicherte Person from a versichertes Objekt at a glance instead
                        of hovering each chip in turn. The kind is DATA (`link.kind`); nothing here is
                        inferred from the label.

                        ACCESSIBLE NAME. Person and object used to be told apart by the pTooltip alone —
                        p-tag renders a non-focusable element, so that distinction was mouse-only and a
                        screen reader was left with "↔ Anna Meier", which is the arrow announced as "left
                        right arrow" followed by a name and no relation. Colour is no better: it is not
                        announced at all. The kind therefore still travels in the accessible name via
                        [pt].root (PrimeNG's Bind directive writes pt.root onto the p-tag host element),
                        and the tooltip stays for sighted mouse users.

                        role="img" is what makes that name reliable: an element with no role maps to the
                        generic role, and ARIA prohibits naming a generic — the aria-label would simply be
                        dropped by most screen readers. As an image the chip is announced as ONE node
                        carrying its label, which also silences the decorative ↔ inside it.
                    -->
                    @for (link of card.crossLinks; track link.targetKey + link.kind) {
                        <p-tag [value]="'↔ ' + link.label"
                               [severity]="crossLinkSeverity(link)"
                               styleClass="white-space-nowrap"
                               [pt]="crossLinkPt(link)"
                               [pTooltip]="link.kind === 'person' ? 'Versicherte Person' : 'Versichertes Objekt'"></p-tag>
                    }
                </div>
            }

            <!--
                The expander, and beside it what neither the card nor the expander shows.

                The button is a p-button and not a clickable div, so Space and Enter activate it, it is
                reachable by keyboard and it carries the theme's own focus ring. Rendered ONLY when there
                is something behind it — a dead "0 weitere Angaben" is worse than no control at all.
                aria-expanded / aria-controls travel through [pt].root, which is the only way onto the
                element that actually carries the button role.

                No `severity="secondary"`: it is an ACTION, so it wears the theme's primary colour like
                every other action in the app. As a grey text button it read as a caption and was
                routinely missed — and the count behind it is the answer to "ist das alles?", the
                question this whole screen exists for.

                level3Count is stated rather than silently dropped, and stays SECONDARY: it is not
                clickable, and giving it the action colour would offer a control that does not exist. A
                reader who expands the card and STILL does not find the field they came for has, without
                this number, no way of knowing that it exists one view further on: they would conclude it
                was never delivered, which is the "es wurde nicht importiert" claim this screen was built
                to refute.

                The row carries the top margin as a PrimeFlex utility instead of a SCSS declaration, and
                holds both children so the count sits beside the control it qualifies. No separator glyph
                between them — the gap says it, and a "·" would dangle whenever one of the two is absent.
            -->
            @if (card.level2.length || card.level3Count) {
                <div class="flex flex-wrap align-items-center gap-2 mt-1">

                    <!--
                        The label follows `open`, not `expanded`: while a term is active the block may
                        be on screen because the search put it there, and a control reading "3 weitere
                        Angaben →" over pairs that are already visible is a control that lies about
                        what it does.
                    -->
                    @if (card.level2.length) {
                        <p-button [label]="open ? 'Weniger anzeigen' : card.level2.length + ' weitere Angaben →'"
                                  size="small"
                                  [text]="true"
                                  styleClass="cm-ed-more"
                                  [pt]="foldPt"
                                  (onClick)="toggle()"></p-button>
                    }

                    @if (card.level3Count) {
                        <span class="text-xs text-color-secondary white-space-nowrap">{{card.level3Count}} nicht angezeigt</span>
                    }

                </div>
            }

            <!--
                The controlled region is ALWAYS in the document and only its content is conditional: an
                aria-controls pointing at an element that does not exist yet is a dangling reference for
                assistive technology.

                WEITERE ANGABEN, and it now SAYS SO. Expanding a card used to reveal pairs in exactly
                the type of the level-1 pairs above them — more of the same, with nothing to tell a
                reader that the basics had ended. The run label names the level and the emphasis step
                (lighter weight, same tone) shows it, so the distinction survives even where the label
                scrolls out of view.

                The label carries its own rule, which is why the old `pt-2 border-top-1` is gone: a
                divider AND a labelled rule 4 px apart is two boundaries for one boundary.

                AUTO-REVEAL. `open` is the reader's own state OR, while a term is active, the search
                having found one of its matches inside these very pairs (see the component's `open`).
                A match sitting behind a closed expander is a match the reader is never shown, on a
                screen whose entire promise is that nothing is hidden — and with no dimming to point at
                the card, an unopened one would look exactly like a card with nothing in it.

                The reveal is temporary and NEVER written back: clearing the term returns the card to
                the state its reader left it in, and a click always wins over the automatic state.
            -->
            <div [attr.id]="level2Id">
                @if (open) {
                    <div class="mt-1">
                        <mvs-cm-ed-kv-list [fields]="card.level2"
                                           [size]="size"
                                           emphasis="weitere"
                                           runLabel="Weitere Angaben"
                                           [searching]="searching"></mvs-cm-ed-kv-list>
                    </div>
                }
            </div>

        </div>

    </div>
}
